import {
  IRynReplayEnvelope,
  RYN_REPLAY_SERVER_MAX_DECOMPRESSED_BYTES,
  rynReplayEnvelopeBytes,
} from './ryn-replay-batcher';
import { RYN_REPLAY_FULL_SNAPSHOT_EVENT_TYPE } from './ryn-replay-event-types';

/**
 * Budget for a decompressed JSON batch body.
 *
 * Slightly under the server's `MaxDecompressedBytes` (15 MB) so array brackets
 * and compressor framing cannot tip a "just under" batch into a 413. Safety
 * FullSnapshots with `inlineStylesheet: true` routinely exceed the old 5 MB
 * cap; omitting them left sessions with only incrementals and the player
 * error "missing its opening snapshot".
 */
export const RYN_REPLAY_INGEST_BYTE_BUDGET = Math.floor(
  RYN_REPLAY_SERVER_MAX_DECOMPRESSED_BYTES * 0.95
);

/**
 * Replace data-URLs only. Stylesheet / CSS text must survive so
 * `inlineStylesheet: true` capture actually looks styled in the player —
 * truncating long CSS undoes inlining and leaves Safety/HSE as naked HTML.
 */
export const stripHeavyRynReplayValue = (value: unknown): unknown => {
  if (typeof value === 'string') {
    if (value.startsWith('data:')) {
      return '[data-url omitted]';
    }
    return value;
  }
  if (Array.isArray(value)) {
    return value.map(stripHeavyRynReplayValue);
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, child] of Object.entries(
      value as Record<string, unknown>
    )) {
      out[key] = stripHeavyRynReplayValue(child);
    }
    return out;
  }
  return value;
};

/**
 * Force an envelope under `maxBytes`. Prefer stripping data-URLs; if that
 * is not enough, non-snapshot envelopes become an omission marker so the
 * batch can still 202. FullSnapshots are never omitted — dropping the
 * keyframe makes the whole session unplayable.
 */
export const slimRynReplayEnvelope = (
  envelope: IRynReplayEnvelope,
  maxBytes: number = RYN_REPLAY_INGEST_BYTE_BUDGET
): IRynReplayEnvelope => {
  if (rynReplayEnvelopeBytes(envelope) <= maxBytes) {
    return envelope;
  }

  const stripped: IRynReplayEnvelope = {
    ...envelope,
    payload: stripHeavyRynReplayValue(envelope.payload) as Record<
      string,
      unknown
    >,
  };
  if (rynReplayEnvelopeBytes(stripped) <= maxBytes) {
    return stripped;
  }

  // Keyframe: keep the stripped body even if still over budget. A 413/retry
  // is recoverable; shipping `omitted` produces "missing its opening snapshot".
  if (envelope.type === RYN_REPLAY_FULL_SNAPSHOT_EVENT_TYPE) {
    return stripped;
  }

  const originalBytes = rynReplayEnvelopeBytes(envelope);
  return {
    ...envelope,
    payload: {
      omitted: true,
      reason: 'exceeds-ingest-cap',
      originalType: envelope.type,
      originalBytes,
      maxBytes,
    },
  };
};

/**
 * Pack envelopes into one or more batches that each serialize under `maxBytes`.
 * Every envelope is slimmed first. A lone over-budget FullSnapshot is sent as
 * its own batch (may 413 until the server cap is raised).
 */
export const fitRynReplayBatchesForIngest = (
  envelopes: IRynReplayEnvelope[],
  maxBytes: number = RYN_REPLAY_INGEST_BYTE_BUDGET
): IRynReplayEnvelope[][] => {
  if (envelopes.length === 0) {
    return [];
  }

  const slimmed = envelopes.map((e) => slimRynReplayEnvelope(e, maxBytes));
  const batches: IRynReplayEnvelope[][] = [];
  let current: IRynReplayEnvelope[] = [];

  for (const envelope of slimmed) {
    const trial = current.length === 0 ? [envelope] : [...current, envelope];
    if (JSON.stringify(trial).length > maxBytes && current.length > 0) {
      batches.push(current);
      current = [envelope];
      continue;
    }
    current = trial;
  }

  if (current.length > 0) {
    batches.push(current);
  }

  return batches;
};
